// Expertise — storage and matching.
//
// STORAGE. A member's expertise is a list of vocabulary ids (see
// constants/expertise.js) kept on the member row under `skills`. It is NOT
// called `expertise`: GWBIPeople.js already reads `p.expertise` as an alias for
// `p.themes` when building the "Topic of expertise" filter, so a field of that
// name would silently merge into the theme filter and corrupt it. The label the
// member sees is still "Expertise" everywhere.
//
// The `skills` column belongs to the Flask API, which is a separate repo. Until
// it exists, saveExpertise falls back to a per-user localStorage mirror so the
// tool is usable and nothing is lost; it reports which of the two happened so
// the UI can be honest about it. Once the column lands, saves persist for real
// with no change here.
//
// MATCHING. Three modes, by how many members have answered:
//   profile   — almost nobody has: rank on themes, regions and profile text
//   mixed     — some have: expertise where present, profile signal elsewhere
//   expertise — 100+ have: expertise only, the rest of the profile ignored
// Shared skills are weighted by rarity: two people sharing "authenticity &
// traceability" is a far stronger signal than two sharing "survey design".

import { buildApiUrl, createHeaders } from "./apiClient";
import { getCachedUserBundle } from "./authService";
import { parseThemeIds } from "../dashboard/utils/profileUtils";
import {
  EXPERTISE_BY_ID,
  EXPERTISE_ONLY_THRESHOLD,
  isAcademicProfile,
  sanitizeExpertiseIds,
} from "../constants/expertise";

// Enough answers to be worth blending in at all. Below this the expertise
// signal is noise and the tool ranks on the rest of the profile.
export const MIXED_MODE_THRESHOLD = 10;

const LOCAL_KEY = "gwbi_expertise_v1";

/**
 * Whether the signed-in member sees Expertise.
 *
 * Reads the cached profile rather than taking a prop, because the entry points
 * are scattered across pages that do not all load a profile of their own.
 *
 * Checks every shape the cache has taken: Login writes {user, profile} but
 * falls back to profile:null when the server answer is unusable, and
 * getCachedUserBundle then hands back the wrapper itself as `profile`. Testing
 * all three means an older or degraded snapshot hides the entry points only
 * when the member really is not an academic, rather than whenever `type`
 * happens not to have been cached.
 */
export const isAcademicViewer = () => {
  const bundle = getCachedUserBundle();
  if (!bundle) return false;
  return [bundle.profile, bundle.user, bundle.cached].some((candidate) =>
    isAcademicProfile(candidate)
  );
};

/* ------------------------------------------------------------------ *
 * Reading a member's expertise off an API row
 * ------------------------------------------------------------------ */

// Accepts the shapes the API has historically returned for list columns:
// a real array, a JSON-encoded array, or a comma/semicolon separated string.
const parseIdList = (value) => {
  if (value == null) return [];
  if (Array.isArray(value)) {
    return value.flatMap(parseIdList);
  }
  if (typeof value === "object") {
    return parseIdList(value.id ?? value.value ?? value.key ?? value.slug);
  }
  const text = String(value).trim();
  if (!text) return [];
  if (
    (text.startsWith("[") && text.endsWith("]")) ||
    (text.startsWith("{") && text.endsWith("}"))
  ) {
    try {
      return parseIdList(JSON.parse(text));
    } catch (_) {
      // fall through to the separator split
    }
  }
  return text
    .split(/[;,|]/)
    .map((part) => part.trim())
    .filter(Boolean);
};

// Note the absent `row.expertise`: that key means themes elsewhere in the app.
export const readExpertiseFromRow = (row = {}) =>
  sanitizeExpertiseIds(
    parseIdList(row?.skills ?? row?.expertise_skills ?? row?.skills_csv)
  );

/* ------------------------------------------------------------------ *
 * The localStorage mirror (used until the API column exists)
 * ------------------------------------------------------------------ */

const readLocalStore = () => {
  try {
    const raw = window.localStorage.getItem(LOCAL_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (_) {
    return {};
  }
};

export const readLocalExpertise = (userId) => {
  if (!userId) return [];
  return sanitizeExpertiseIds(readLocalStore()[String(userId)] || []);
};

const writeLocalExpertise = (userId, skillIds) => {
  if (!userId) return;
  try {
    const store = readLocalStore();
    store[String(userId)] = skillIds;
    window.localStorage.setItem(LOCAL_KEY, JSON.stringify(store));
  } catch (_) {
    // Private windows and blocked site data: the save simply does not persist.
  }
};

/* ------------------------------------------------------------------ *
 * Load / save for the signed-in member
 * ------------------------------------------------------------------ */

const PROFILE_ENDPOINT = "/wineries/profile";

const fetchOwnProfile = async (userId) => {
  const response = await fetch(
    buildApiUrl(`${PROFILE_ENDPOINT}?user_id=${encodeURIComponent(userId)}`),
    { method: "GET", headers: createHeaders({ json: false, userId }) }
  );
  if (!response.ok) throw new Error(`Could not load profile (${response.status})`);
  const payload = await response.json().catch(() => ({}));
  return payload?.profile || payload || {};
};

// Server value wins when present; the local mirror answers for everyone whose
// save has not yet had a column to land in.
export const loadMyExpertise = async (userId) => {
  if (!userId) return { skillIds: [], source: "none" };
  try {
    const profile = await fetchOwnProfile(userId);
    const fromServer = readExpertiseFromRow(profile);
    if (fromServer.length) return { skillIds: fromServer, source: "server" };
  } catch (_) {
    // Fall through to the mirror — an offline read should not empty the picker.
  }
  const local = readLocalExpertise(userId);
  return { skillIds: local, source: local.length ? "local" : "none" };
};

/**
 * Persist a member's expertise.
 *
 * Sends the profile back untouched apart from `skills`, so a backend that does
 * not yet know the column can reject the write without anything being lost. The
 * returned `persisted` says where the answer actually ended up:
 *   "server" — the API echoed the skills back
 *   "local"  — kept in this browser only; the API has no column yet
 */
export const saveMyExpertise = async (userId, rawSkillIds) => {
  const skillIds = sanitizeExpertiseIds(rawSkillIds);
  if (!userId) {
    return { skillIds, persisted: "local", error: "No signed-in member." };
  }

  writeLocalExpertise(userId, skillIds);

  try {
    const current = await fetchOwnProfile(userId);
    const bundle = getCachedUserBundle();
    const user = { ...(bundle?.user || {}), id: userId };
    const body = {
      user,
      profile: { ...current, id: current?.id ?? userId, skills: skillIds },
    };
    const response = await fetch(buildApiUrl(PROFILE_ENDPOINT), {
      method: "PUT",
      headers: createHeaders({ userId }),
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      return { skillIds, persisted: "local" };
    }
    const payload = await response.json().catch(() => ({}));
    // PUT /wineries/profile answers {ok, user}; GET answers {user, profile}.
    // Check both shapes, or a real save reads as a failed one.
    const echoed = readExpertiseFromRow(
      payload?.profile || payload?.user || payload || {}
    );
    // A backend that quietly drops unknown keys returns 200 and no skills. That
    // is not a real save, so do not claim it was one.
    return { skillIds, persisted: echoed.length || !skillIds.length ? "server" : "local" };
  } catch (_) {
    return { skillIds, persisted: "local" };
  }
};

/* ------------------------------------------------------------------ *
 * Candidates
 * ------------------------------------------------------------------ */

const textOf = (row) =>
  [
    row?.headline,
    row?.biography,
    row?.organization,
    row?.type,
    row?.city,
    Array.isArray(row?.industry_categories)
      ? row.industry_categories.join(" ")
      : row?.industry_categories,
    Array.isArray(row?.winery_roles) ? row.winery_roles.join(" ") : row?.winery_roles,
  ]
    .filter(Boolean)
    .join(" ");

const nameOf = (row) => {
  const first = String(row?.first_name || "").trim();
  const last = String(row?.last_name || "").trim();
  return `${first} ${last}`.trim() || "GWBI member";
};

const mapCandidate = (row = {}) => ({
  id: row?.id ?? row?.user_id ?? null,
  public_id: row?.public_id || null,
  name: nameOf(row),
  type: String(row?.type || "").trim(),
  organization: String(row?.organization || "").trim(),
  country: String(row?.country || "").trim(),
  headline: String(row?.headline || "").trim(),
  avatar_url: row?.avatar_url || "",
  skillIds: readExpertiseFromRow(row),
  themeIds: parseThemeIds(row?.themes).map(String),
  regions: parseIdList(row?.regions),
  text: textOf(row),
  allow_access: row?.allow_access !== false,
  visibility: String(row?.profile_visibility || "public").trim().toLowerCase(),
});

/**
 * Every member who may appear in a match list. Same source and the same
 * visibility rules as GWBI People: one unfiltered fetch, filtered here.
 */
export const listExpertiseCandidates = async () => {
  const response = await fetch(buildApiUrl("/wineries/auth.users"), {
    method: "GET",
    headers: createHeaders({ json: false }),
  });
  if (!response.ok) {
    throw new Error(`Could not load members (${response.status})`);
  }
  const payload = await response.json().catch(() => []);
  const rows = Array.isArray(payload) ? payload : payload?.items || [];
  return rows
    .map(mapCandidate)
    .filter((c) => c.id != null && c.allow_access && c.visibility !== "private");
};

/* ------------------------------------------------------------------ *
 * Match mode
 * ------------------------------------------------------------------ */

export const countExpertiseProfiles = (candidates = []) =>
  candidates.filter((c) => (c.skillIds || []).length > 0).length;

export const pickMatchMode = (coverage = 0) => {
  if (coverage >= EXPERTISE_ONLY_THRESHOLD) return "expertise";
  if (coverage >= MIXED_MODE_THRESHOLD) return "mixed";
  return "profile";
};

// How much each signal counts, per mode. In "expertise" the rest of the profile
// is ignored outright, which is what was asked for: past 100 answers the
// expertise list is better evidence than a biography.
const MODE_WEIGHTS = {
  profile: { expertise: 0, profile: 1 },
  mixed: { expertise: 0.65, profile: 0.35 },
  expertise: { expertise: 1, profile: 0 },
};

/* ------------------------------------------------------------------ *
 * Keyword handling (the "profile" signal)
 * ------------------------------------------------------------------ */

const STOPWORDS = new Set([
  "about", "after", "also", "and", "are", "associate", "been", "both", "from",
  "have", "into", "member", "more", "most", "over", "professor", "research",
  "researcher", "senior", "such", "that", "the", "their", "them", "then",
  "there", "these", "they", "this", "through", "university", "using", "were",
  "what", "when", "where", "which", "while", "with", "wine", "wines", "winery",
  "wineries", "work", "working", "your",
]);

const keywordsOf = (text = "") =>
  new Set(
    String(text)
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .split(/\s+/)
      .filter((word) => word.length >= 4 && !STOPWORDS.has(word))
  );

/**
 * The seeker's own papers, used only to widen their keyword set. Best-effort:
 * papers live behind three different endpoints depending on deployment age, and
 * a member with none is the normal case, so every failure is silent.
 */
export const fetchOwnPaperKeywords = async (userId) => {
  if (!userId) return [];
  const paths = [
    `/articles/documents?user_id=${encodeURIComponent(userId)}`,
    `/articles/list?user_id=${encodeURIComponent(userId)}`,
    `/documents?schema=articles&table=documents&user_id=${encodeURIComponent(userId)}`,
  ];
  for (const path of paths) {
    try {
      const response = await fetch(buildApiUrl(path), {
        method: "GET",
        headers: createHeaders({ json: false, userId }),
      });
      if (!response.ok) continue;
      const payload = await response.json().catch(() => null);
      const rows = Array.isArray(payload) ? payload : payload?.items || [];
      if (!rows.length) continue;
      return rows.flatMap((paper) => [
        ...parseIdList(paper?.keywords),
        String(paper?.title || ""),
      ]);
    } catch (_) {
      // try the next shape
    }
  }
  return [];
};

/* ------------------------------------------------------------------ *
 * Ranking
 * ------------------------------------------------------------------ */

// Inverse document frequency over the candidate pool: a skill almost nobody
// claims carries more weight than one half the Institute claims.
const buildRarityWeights = (candidates) => {
  const counts = new Map();
  candidates.forEach((c) =>
    (c.skillIds || []).forEach((id) => counts.set(id, (counts.get(id) || 0) + 1))
  );
  const total = Math.max(1, candidates.length);
  return (id) => Math.log((total + 1) / ((counts.get(id) || 0) + 1)) + 1;
};

const asArray = (value) =>
  Array.isArray(value) ? value : parseIdList(value);

/**
 * Everything a comparison needs, derived once from the pool and the seeker.
 *
 * Built in one place so the tool's match list, the "% similar" figure on a
 * profile and the expertise search all produce the same number for the same
 * pair. Two screens disagreeing about how close two members are is worse than
 * either number being slightly off.
 */
export const buildMatchContext = ({
  candidates = [],
  mySkillIds = [],
  myThemeIds = [],
  myRegions = [],
  myText = "",
  myPaperTerms = [],
  modeOverride = null,
} = {}) => {
  const coverage = countExpertiseProfiles(candidates);
  const mode = modeOverride || pickMatchMode(coverage);
  const skillIds = sanitizeExpertiseIds(mySkillIds);
  return {
    mode,
    coverage,
    weights: MODE_WEIGHTS[mode],
    rarityWeight: buildRarityWeights(candidates),
    mySkills: new Set(skillIds),
    myGroups: new Set(
      skillIds.map((id) => EXPERTISE_BY_ID[id]?.group).filter(Boolean)
    ),
    myThemes: new Set(asArray(myThemeIds).map(String)),
    myRegionSet: new Set(
      asArray(myRegions)
        .map((r) => String(r).trim().toLowerCase())
        .filter(Boolean)
    ),
    myKeywords: keywordsOf([myText, ...myPaperTerms].filter(Boolean).join(" ")),
    // The ceiling a candidate could reach on skills alone, so a percentage is
    // relative to something real rather than an arbitrary constant.
    bestPossibleSkillScore:
      skillIds.reduce((sum, id) => sum + buildRarityWeights(candidates)(id), 0) ||
      1,
  };
};

/** Score one member against the seeker described by `ctx`. */
export const scoreCandidate = (candidate, ctx) => {
  const reasons = [];

  /* — expertise signal — */
  const shared = (candidate.skillIds || []).filter((id) => ctx.mySkills.has(id));
  let skillScore = shared.reduce((sum, id) => sum + ctx.rarityWeight(id), 0);
  // A neighbour in the same group is a partial match: someone doing TDS and
  // someone doing CATA are closer than the raw id comparison suggests.
  const adjacent = (candidate.skillIds || []).filter(
    (id) => !ctx.mySkills.has(id) && ctx.myGroups.has(EXPERTISE_BY_ID[id]?.group)
  );
  skillScore += adjacent.length * 0.25;
  const expertiseNorm = Math.min(1, skillScore / ctx.bestPossibleSkillScore);
  if (shared.length) {
    reasons.push({
      kind: "expertise",
      label: `Shares ${shared.length} ${
        shared.length === 1 ? "expertise" : "expertise areas"
      }`,
      items: shared.map((id) => EXPERTISE_BY_ID[id]?.label).filter(Boolean),
    });
  } else if (adjacent.length) {
    reasons.push({
      kind: "expertise",
      label: "Works with related methods",
      items: adjacent.slice(0, 3).map((id) => EXPERTISE_BY_ID[id]?.label).filter(Boolean),
    });
  }

  /* — profile signal — */
  let profileScore = 0;
  const sharedThemes = (candidate.themeIds || []).filter((id) =>
    ctx.myThemes.has(String(id))
  );
  if (sharedThemes.length) {
    profileScore += Math.min(3, sharedThemes.length) * 1.4;
    reasons.push({
      kind: "theme",
      label: `${sharedThemes.length} shared ${
        sharedThemes.length === 1 ? "theme" : "themes"
      }`,
      items: [],
    });
  }
  const sharedRegions = (candidate.regions || []).filter((r) =>
    ctx.myRegionSet.has(String(r).trim().toLowerCase())
  );
  if (sharedRegions.length) {
    profileScore += 1;
    reasons.push({ kind: "region", label: "Same region", items: sharedRegions });
  }
  const candidateKeywords = keywordsOf(candidate.text);
  const overlap = [...ctx.myKeywords].filter((word) => candidateKeywords.has(word));
  if (overlap.length) {
    profileScore += Math.min(3, overlap.length * 0.5);
    reasons.push({
      kind: "keyword",
      label: "Similar profile wording",
      items: overlap.slice(0, 4),
    });
  }
  const profileNorm = Math.min(1, profileScore / 7);

  const score =
    ctx.weights.expertise * expertiseNorm + ctx.weights.profile * profileNorm;

  return {
    ...candidate,
    score,
    confidence: Math.round(score * 100),
    sharedSkillIds: shared,
    matchedOn: ctx.weights.expertise > 0 && shared.length ? "expertise" : "profile",
    reasons,
  };
};

/**
 * Rank members against the seeker and return the best few.
 *
 * @returns {{mode: string, coverage: number, matches: Array}}
 */
export const rankExpertiseMatches = ({
  candidates = [],
  mySkillIds = [],
  myThemeIds = [],
  myRegions = [],
  myText = "",
  myPaperTerms = [],
  myUserId = null,
  limit = 5,
} = {}) => {
  const pool = candidates.filter((c) => String(c.id) !== String(myUserId ?? ""));
  const ctx = buildMatchContext({
    candidates: pool,
    mySkillIds,
    myThemeIds,
    myRegions,
    myText,
    myPaperTerms,
  });

  const matches = pool
    .map((candidate) => scoreCandidate(candidate, ctx))
    .filter((c) => c.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, Math.max(1, limit));

  return { mode: ctx.mode, coverage: ctx.coverage, matches };
};

/**
 * How close one specific member is to the seeker, as the percentage shown on
 * their profile. Same context and same scoring as the match list, so the number
 * on a profile agrees with the number in the tool.
 */
export const scoreExpertiseSimilarity = ({
  candidates = [],
  target = null,
  mySkillIds = [],
  myThemeIds = [],
  myRegions = [],
  myText = "",
  myPaperTerms = [],
  myUserId = null,
} = {}) => {
  if (!target) return null;
  const pool = candidates.filter((c) => String(c.id) !== String(myUserId ?? ""));
  const ctx = buildMatchContext({
    candidates: pool,
    mySkillIds,
    myThemeIds,
    myRegions,
    myText,
    myPaperTerms,
  });
  const scored = scoreCandidate(target, ctx);
  return {
    mode: ctx.mode,
    coverage: ctx.coverage,
    confidence: scored.confidence,
    sharedSkillIds: scored.sharedSkillIds,
    reasons: scored.reasons,
    matchedOn: scored.matchedOn,
  };
};

/* ------------------------------------------------------------------ *
 * Searching members by chosen expertise
 * ------------------------------------------------------------------ */

// Words that appear in so many expertise labels that matching on them says
// nothing about whether a member does that work. The taxonomy is method-heavy
// and repeats these constantly — "analysis" alone is in a fifth of the labels —
// so without this the fallback matches almost everyone on almost everything.
const LABEL_NOISE = new Set([
  "analysis", "assessment", "data", "design", "development", "evaluation",
  "management", "measures", "methods", "modelling", "models", "planning",
  "research", "science", "sector", "studies", "systems", "testing", "theory",
]);

const labelKeywords = (id) => {
  const label = EXPERTISE_BY_ID[id]?.label || "";
  return [...keywordsOf(label)].filter((word) => !LABEL_NOISE.has(word));
};

/**
 * Find the members who fit a chosen set of expertise.
 *
 * Uses the same three modes as everything else, for the reason the tool does:
 * until enough members have filled expertise in, a search that only looked at
 * the column would report almost nobody. Below the threshold it falls back to
 * reading the chosen skills' own wording against each member's profile text and
 * theme labels, and says which it did.
 *
 * @returns {{mode, coverage, total, matches}} `total` counts everyone who fits,
 *          `matches` holds the top `limit` of them.
 */
export const searchMembersByExpertise = ({
  candidates = [],
  selectedIds = [],
  myUserId = null,
  themeLabelById = {},
  limit = 5,
} = {}) => {
  const wanted = sanitizeExpertiseIds(selectedIds);
  const pool = candidates.filter((c) => String(c.id) !== String(myUserId ?? ""));
  const coverage = countExpertiseProfiles(pool);
  const mode = pickMatchMode(coverage);
  const weights = MODE_WEIGHTS[mode];

  if (!wanted.length) {
    return { mode, coverage, total: 0, matches: [] };
  }

  const rarityWeight = buildRarityWeights(pool);
  const wantedSet = new Set(wanted);
  const wantedGroups = new Set(
    wanted.map((id) => EXPERTISE_BY_ID[id]?.group).filter(Boolean)
  );
  const ceiling = wanted.reduce((sum, id) => sum + rarityWeight(id), 0) || 1;
  const wantedTerms = wanted.map((id) => ({ id, words: labelKeywords(id) }));

  const scored = pool.map((candidate) => {
    const reasons = [];

    const shared = (candidate.skillIds || []).filter((id) => wantedSet.has(id));
    let skillScore = shared.reduce((sum, id) => sum + rarityWeight(id), 0);
    const adjacent = (candidate.skillIds || []).filter(
      (id) => !wantedSet.has(id) && wantedGroups.has(EXPERTISE_BY_ID[id]?.group)
    );
    skillScore += adjacent.length * 0.25;
    const expertiseNorm = Math.min(1, skillScore / ceiling);
    if (shared.length) {
      reasons.push({
        kind: "expertise",
        label: `Lists ${shared.length} of these`,
        items: shared.map((id) => EXPERTISE_BY_ID[id]?.label).filter(Boolean),
      });
    } else if (adjacent.length) {
      reasons.push({
        kind: "expertise",
        label: "Related expertise",
        items: adjacent.slice(0, 3).map((id) => EXPERTISE_BY_ID[id]?.label).filter(Boolean),
      });
    }

    // Fallback signal: does the member's own wording describe this work? Theme
    // labels are folded in because a member who never wrote a biography may
    // still have chosen telling themes.
    const themeText = (candidate.themeIds || [])
      .map((id) => themeLabelById[String(id)] || "")
      .join(" ");
    const words = keywordsOf(`${candidate.text} ${themeText}`);
    const hits = wantedTerms.filter(
      (term) => term.words.length && term.words.some((word) => words.has(word))
    );
    const textNorm = Math.min(1, hits.length / Math.min(3, wantedTerms.length));
    if (hits.length) {
      reasons.push({
        kind: "keyword",
        label: "Profile describes this work",
        items: hits.slice(0, 3).map((h) => EXPERTISE_BY_ID[h.id]?.label).filter(Boolean),
      });
    }

    const score = weights.expertise * expertiseNorm + weights.profile * textNorm;
    return {
      ...candidate,
      score,
      confidence: Math.round(score * 100),
      sharedSkillIds: shared,
      matchedOn: weights.expertise > 0 && shared.length ? "expertise" : "profile",
      reasons,
    };
  });

  const fitting = scored
    .filter((c) => c.score > 0)
    .sort((a, b) => b.score - a.score);

  return {
    mode,
    coverage,
    total: fitting.length,
    // limit <= 0 means "every member who fits" — the search page lists them
    // all, the profile panel shows a top few.
    matches: limit > 0 ? fitting.slice(0, limit) : fitting,
  };
};

const expertiseService = {
  isAcademicViewer,
  readExpertiseFromRow,
  readLocalExpertise,
  loadMyExpertise,
  saveMyExpertise,
  listExpertiseCandidates,
  countExpertiseProfiles,
  pickMatchMode,
  fetchOwnPaperKeywords,
  rankExpertiseMatches,
  scoreExpertiseSimilarity,
  searchMembersByExpertise,
  buildMatchContext,
  scoreCandidate,
};

export default expertiseService;
